import React, { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import axios from "axios"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import withAuth from "../withAuth"
import { baseUrl } from "../../Network"
import { handleAuthError } from "../../utils"
import { actionLabel, apiErrorMessage, describeAnswer, formatDateTime, formatDuration, typeLabel } from "./aiTestShared"

const LIMIT = 25

const STATUS_LABEL = { in_progress: "In progress", submitted: "Submitted", expired: "Time ran out" }
const STATUS_COLOR = { in_progress: "warning", submitted: "success", expired: "secondary" }

/** Readable learner answer, including match-pair progress (matched / missed). */
const learnerAnswerText = (row) => {
  const q = row.question
  const a = row.answer
  if (!a) return "Not answered"
  if (q.type === "match_pairs") {
    if (a.value && typeof a.value === "object") return describeAnswer(q, a.value)
    const missed = (a.missed || []).length
    return `${(a.matched || []).length} of ${(q.left || []).length} matched${missed ? `, ${missed} with a wrong try first` : ""}`
  }
  return describeAnswer(q, a.value)
}

const ManageAiTestAttempts = () => {
  document.title = "Test attempts | RYD Admin"
  const [searchParams] = useSearchParams()
  const [tests, setTests] = useState([])
  const [filters, setFilters] = useState({
    testId: searchParams.get("testId") || "",
    search: "",
    status: "",
    from: "",
    to: "",
  })
  const [applied, setApplied] = useState(filters)
  const [page, setPage] = useState(1)
  const [data, setData] = useState({ rows: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)

  useEffect(() => {
    axios
      .get(`${baseUrl}/admin/ai-test/all`)
      .then((res) => setTests(Array.isArray(res?.data?.data) ? res.data.data : []))
      .catch(handleAuthError)
  }, [])

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) })
      Object.entries(applied).forEach(([k, v]) => {
        if (v) params.set(k, v)
      })
      const res = await axios.get(`${baseUrl}/admin/ai-test/attempts?${params.toString()}`)
      if (res?.data?.status) setData({ rows: res.data.data?.rows || [], total: res.data.data?.total || 0 })
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not load attempts"))
    } finally {
      setLoading(false)
    }
  }, [applied, page])

  useEffect(() => {
    load()
  }, [load])

  const apply = (e) => {
    e?.preventDefault()
    setPage(1)
    setApplied({ ...filters })
  }

  const reset = () => {
    const empty = { testId: "", search: "", status: "", from: "", to: "" }
    setFilters(empty)
    setPage(1)
    setApplied(empty)
  }

  const openDetail = async (row) => {
    if (detailLoading) return
    try {
      setDetailLoading(true)
      setDetail({ id: row.id })
      const res = await axios.get(`${baseUrl}/admin/ai-test/attempt/${row.id}`)
      if (res?.data?.status) setDetail(res.data.data)
    } catch (e) {
      handleAuthError(e)
      toast.error(apiErrorMessage(e, "Could not open the attempt"))
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  const pages = Math.max(1, Math.ceil(data.total / LIMIT))
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }))
  const rec = detail?.recommendation

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Interactive tests" breadcrumbItem="Attempts" />
          <div className="d-flex flex-wrap gap-2 justify-content-end align-items-center mb-3">
            <Button tag={Link} to="/ai/tests" color="light" size="sm">
              All tests
            </Button>
          </div>
          <Card>
            <CardBody>
              <form onSubmit={apply}>
                <Row className="g-2 mb-3 align-items-end">
                  <Col md={3}>
                    <Label className="small mb-1">Test</Label>
                    <Input type="select" bsSize="sm" value={filters.testId} onChange={set("testId")}>
                      <option value="">All tests</option>
                      {tests.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={3}>
                    <Label className="small mb-1">Learner</Label>
                    <Input bsSize="sm" value={filters.search} onChange={set("search")} placeholder="Name or email" />
                  </Col>
                  <Col md={2}>
                    <Label className="small mb-1">Status</Label>
                    <Input type="select" bsSize="sm" value={filters.status} onChange={set("status")}>
                      <option value="">Any</option>
                      {Object.entries(STATUS_LABEL).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={1}>
                    <Label className="small mb-1">From</Label>
                    <Input type="date" bsSize="sm" value={filters.from} onChange={set("from")} />
                  </Col>
                  <Col md={1}>
                    <Label className="small mb-1">To</Label>
                    <Input type="date" bsSize="sm" value={filters.to} onChange={set("to")} />
                  </Col>
                  <Col md={2} className="d-flex gap-1">
                    <Button type="submit" size="sm" color="primary" disabled={loading}>
                      {loading ? <Spinner size="sm" /> : "Filter"}
                    </Button>
                    <Button type="button" size="sm" color="light" onClick={reset} disabled={loading}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </form>

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-middle table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Learner</th>
                        <th>Email</th>
                        <th>Test</th>
                        <th>Started</th>
                        <th>Duration</th>
                        <th>Score</th>
                        <th>%</th>
                        <th>Grade</th>
                        <th>Passed</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.map((r) => (
                        <tr key={r.id}>
                          <td>{r.parentName || `Parent #${r.parentId}`}</td>
                          <td className="small">{r.parentEmail || "—"}</td>
                          <td className="small">{r.testTitle || "Deleted test"}</td>
                          <td className="small text-nowrap">{formatDateTime(r.startedAt)}</td>
                          <td className="small">{formatDuration(r.durationSeconds)}</td>
                          <td className="small">{r.score == null ? "—" : `${r.score} / ${r.maxScore}`}</td>
                          <td>{r.percent == null ? "—" : `${r.percent}%`}</td>
                          <td className="fw-bold">{r.grade || "—"}</td>
                          <td>{r.passed == null ? "—" : r.passed ? <Badge color="success">Yes</Badge> : <Badge color="danger">No</Badge>}</td>
                          <td>
                            <Badge color={STATUS_COLOR[r.status] || "light"}>{STATUS_LABEL[r.status] || r.status}</Badge>
                          </td>
                          <td className="text-end">
                            <Button size="sm" color="secondary" outline onClick={() => openDetail(r)}>
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!data.rows.length && (
                        <tr>
                          <td colSpan={11} className="text-center text-muted py-4">
                            No attempts match these filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  {data.total} attempt{data.total === 1 ? "" : "s"}
                </small>
                <div className="d-flex gap-2 align-items-center">
                  <Button size="sm" color="light" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </Button>
                  <small>
                    Page {page} of {pages}
                  </small>
                  <Button size="sm" color="light" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={!!detail} toggle={() => setDetail(null)} size="xl" scrollable>
        <ModalHeader toggle={() => setDetail(null)}>
          {detail?.testTitle || "Attempt"} {detail?.parentName ? `— ${detail.parentName}` : ""}
        </ModalHeader>
        <ModalBody>
          {detailLoading || !detail?.review ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <Row className="mb-3 g-2">
                <Col md={3}>
                  <div className="small text-muted">Score</div>
                  <div className="fw-bold">
                    {detail.score == null ? "—" : `${detail.score} / ${detail.maxScore} (${detail.percent}%)`}
                  </div>
                </Col>
                <Col md={2}>
                  <div className="small text-muted">Grade</div>
                  <div className="fw-bold">{detail.grade || "—"}</div>
                </Col>
                <Col md={2}>
                  <div className="small text-muted">Status</div>
                  <Badge color={STATUS_COLOR[detail.status] || "light"}>{STATUS_LABEL[detail.status] || detail.status}</Badge>
                </Col>
                <Col md={2}>
                  <div className="small text-muted">Duration</div>
                  <div>{formatDuration(detail.durationSeconds)}</div>
                </Col>
                <Col md={3}>
                  <div className="small text-muted">Submitted</div>
                  <div>{formatDateTime(detail.submittedAt)}</div>
                </Col>
              </Row>
              {rec && (
                <div className="border rounded p-3 mb-3" style={{ background: "#FBF5FC" }}>
                  <div className="fw-semibold mb-1">
                    Recommendation: {actionLabel(rec.action)}
                    {rec.label ? ` · ${rec.label}` : ""}
                  </div>
                  {rec.message && <div className="small mb-1">{rec.message}</div>}
                  {rec.nextCourse && <div className="small">Next course: {rec.nextCourse.title}</div>}
                  {rec.learningPath && <div className="small">Learning path: {rec.learningPath.title}</div>}
                  {rec.reviewLessons?.length > 0 && (
                    <div className="small">
                      Lessons to review: {rec.reviewLessons.map((l) => l.lessonTitle || l.lessonId || l.courseSlug).join(", ")}
                    </div>
                  )}
                </div>
              )}
              <div className="table-responsive">
                <Table size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Card</th>
                      <th>Learner&apos;s answer</th>
                      <th>Correct answer</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.review.map((row, i) => (
                      <tr key={row.question.id}>
                        <td>{i + 1}</td>
                        <td style={{ maxWidth: 320 }}>
                          <div className="small text-muted">{typeLabel(row.question.type)}</div>
                          <div className="fw-semibold">{row.question.prompt}</div>
                          {row.question.code && (
                            <pre className="small mb-1 p-2 rounded" style={{ background: "#1E1B2E", color: "#E9E4F0", whiteSpace: "pre-wrap" }}>
                              {row.question.code}
                            </pre>
                          )}
                          {row.explanation && <div className="small text-muted">{row.explanation}</div>}
                        </td>
                        <td className="small">{learnerAnswerText(row)}</td>
                        <td className="small">{describeAnswer(row.question, row.correctAnswer)}</td>
                        <td>
                          {!row.answer ? (
                            <Badge color="light" className="text-dark">
                              Skipped
                            </Badge>
                          ) : row.answer.firstTryCorrect ? (
                            <Badge color="success">Correct</Badge>
                          ) : row.answer.correct ? (
                            <Badge color="warning">Correct after {row.answer.tries} tries</Badge>
                          ) : (
                            <Badge color="danger">Wrong</Badge>
                          )}
                          <div className="small text-muted">{row.answer ? `${row.answer.score} pt` : "0 pt"}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDetail(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <ToastContainer />
    </React.Fragment>
  )
}

export default withAuth(ManageAiTestAttempts)
